import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Input, Spinner, Typography } from '@material-tailwind/react'
import { toast } from 'react-toastify'
import { FiSearch } from 'react-icons/fi'
import { PageLayout } from '@/layouts/page-layout'
import { OwnOfficeSelector } from '@/shared/selectors/own-office-selector'
import { AccessResult } from '@/shared/components/access-result'
import { formatDocumentSync } from '@/pages/access-control/gun-mode'
import { useAuthorization } from '@/hooks'
import { ContractorEmployeeAccessServices } from '@/services/contractor-employee-access-services'

export const ContractorEmployeeAccess = () => {
  const navigate = useNavigate()
  const { hasAccess } = useAuthorization({ redirection: { enable: false } })
  const services = useMemo(() => new ContractorEmployeeAccessServices(), [])

  const [canAccess, setCanAccess] = useState(null)
  const [offices, setOffices] = useState([])
  const [officeId, setOfficeId] = useState('all')
  const [documentNumber, setDocumentNumber] = useState('')
  const [result, setResult] = useState(null)
  const [loading, setLoading] = useState(false)
  const [companyResult, setCompanyResult] = useState(null)
  const [companyLoading, setCompanyLoading] = useState(false)
  const [showCompanyStatus, setShowCompanyStatus] = useState(true)
  const [companyStatusAvailable, setCompanyStatusAvailable] = useState(false)

  useEffect(() => {
    hasAccess('contractor_employee_access#list')
      .then(setCanAccess)
      .catch(() => setCanAccess(false))
  }, [])

  useEffect(() => {
    if (!canAccess) return

    services.getOffices()
      .then((items) => {
        setOffices(items)
        if (items.length === 1) setOfficeId(String(items[0].id))
      })
      .catch(() => toast.error('No se pudieron cargar las dependencias'))

    setCompanyLoading(true)
    services.companyCheck()
      .then((response) => {
        setCompanyStatusAvailable(response.applies)
        if (!response.applies) return

        setCompanyResult({
          access: response.access,
          message: response.access ? 'Empresa cumple exigencias' : 'Empresa no cumple exigencias',
          tipo: response.type,
          data: response.data,
          detail: response.detail || null,
          displayDoc: response.data?.document_number
        })
      })
      .catch(() => toast.error('No se pudo cargar el estado de la empresa'))
      .finally(() => setCompanyLoading(false))
  }, [canAccess, services])

  const clearResult = () => {
    if (result) setResult(null)
  }

  const resultMessage = (response) => {
    if (!response.data) return 'No encontrado'

    if (response.type === 'CONTRACTOR_COMPANY') {
      return response.access ? 'Empresa cumple exigencias' : 'Empresa no cumple exigencias'
    }

    return response.access ? 'Cumple exigencias' : 'No cumple exigencias'
  }

  const handleSubmit = async (event) => {
    event.preventDefault()

    const formatted = formatDocumentSync(documentNumber.trim())
    const cleaned = formatted.replace(/[^a-zA-Z0-9]/g, '').toUpperCase()
    if (!cleaned) return toast.error('Ingrese un documento válido')

    setDocumentNumber(formatted)
    setResult(null)
    setShowCompanyStatus(false)
    setLoading(true)

    try {
      const response = await services.checkAccess(cleaned, officeId || 'all')
      setResult({
        access: response.access,
        message: resultMessage(response),
        tipo: response.type,
        data: response.data,
        detail: response.detail || null,
        displayDoc: formatted
      })
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('No tienes permiso para usar este módulo')
        navigate('/dashboard')
        return
      }

      toast.error('No se pudo consultar el cumplimiento')
      setResult({
        access: false,
        message: 'No cumple exigencias',
        detail: null,
        displayDoc: formatted
      })
    } finally {
      setLoading(false)
    }
  }

  if (canAccess === null) {
    return (
      <div className="h-screen flex items-center justify-center">
        <Spinner className="h-10 w-10" />
      </div>
    )
  }

  if (!canAccess) {
    navigate('/dashboard')
    return null
  }

  return (
    <PageLayout
      title="Consulta de Cumplimiento"
      description="Consulta si tus empleados o empresas visibles cumplen las exigencias configuradas por la empresa mandante."
      className="flex flex-col gap-6 h-full"
    >
      <OwnOfficeSelector
        offices={offices}
        officeId={officeId}
        setOfficeId={setOfficeId}
        instructions={false}
      />

      <main className="flex flex-1 flex-col md:flex-row overflow-auto w-full">
        <section className="w-full md:w-1/3 p-4 bg-white">
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div>
              <Typography variant="small" color="blue-gray" className="mb-1">
                Documento de identidad o RUT de empresa
              </Typography>
              <Input
                size="lg"
                placeholder="Ej: 12.345.678-9 o 76.123.456-7"
                value={documentNumber}
                onChange={(event) => {
                  setDocumentNumber(event.target.value)
                  clearResult()
                }}
                className="!border pr-10 focus:!border-blue-500"
                labelProps={{ className: 'hidden' }}
              />
            </div>

            <Button
              type="submit"
              fullWidth
              disabled={loading}
              className="flex items-center justify-center gap-2"
            >
              {loading ? <Spinner className="h-4 w-4" /> : <FiSearch className="h-5 w-5" />}
              Consultar
            </Button>

            {companyStatusAvailable && (
              <Button
                type="button"
                fullWidth
                variant="outlined"
                disabled={showCompanyStatus}
                onClick={() => {
                  setResult(null)
                  setShowCompanyStatus(true)
                }}
              >
                Mostrar estado de empresa
              </Button>
            )}
          </form>
        </section>

        <section className="w-full md:flex-1 p-4 bg-gray-50">
          {showCompanyStatus && (companyLoading || companyResult) && (
            <div className="mb-6">
              <Typography variant="h6" color="blue-gray" className="mb-3">
                Estado de empresa
              </Typography>
              {companyLoading ? <Spinner className="h-6 w-6" /> : <AccessResult result={companyResult} />}
            </div>
          )}

          {!showCompanyStatus && <AccessResult result={result} />}
        </section>
      </main>
    </PageLayout>
  )
}
